import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDateTime } from 'components/IAP/Resources/utils/dateUtils';
import { describeBlockers } from 'components/IAP/utils/actionDependencies';
import type { MyTask } from './useMyTasks';
import {
  assignmentLabel,
  isOverdue,
  type MyTaskGroup,
} from './myTasksTableModel';

const SEARCH_LABEL = 'Search your tasks…';
const COLUMNS = 5;

const PRIORITY_VARIANT: Record<
  string,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  Critical: 'destructive',
  High: 'default',
  Normal: 'secondary',
  Low: 'outline',
};

/**
 * Presentational: takes groups that are already filtered and ordered, so it
 * stays free of the query chain behind My Tasks.
 *
 * Grouped by where the work lives rather than sorted into one flat list,
 * because "which incident is this for" is the first thing you need in order to
 * act on a task at all.
 */
export function MyTasksTable({
  groups,
  search,
  onSearchChange,
  onOpenTask,
  onOpenGroup,
  emptyMessage,
}: {
  groups: MyTaskGroup[];
  search: string;
  onSearchChange: (next: string) => void;
  onOpenTask: (task: MyTask) => void;
  onOpenGroup: (group: MyTaskGroup) => void;
  emptyMessage: string;
}) {
  return (
    <div className="tw:space-y-2">
      <Input
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder={SEARCH_LABEL}
        aria-label={SEARCH_LABEL}
        className="tw:h-8"
      />
      <div className="tw:overflow-x-auto tw:rounded-md tw:border tw:border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Task</TableHead>
              <TableHead className="tw:w-[120px]">Priority</TableHead>
              <TableHead className="tw:w-[140px]">Status</TableHead>
              <TableHead className="tw:w-[180px]">Due</TableHead>
              <TableHead className="tw:w-[200px]">Assignment</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={COLUMNS}
                  className="tw:text-xs tw:text-muted-foreground"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              groups.map((group) => (
                <React.Fragment key={group.key}>
                  <TableRow className="tw:bg-muted/50 tw:hover:bg-muted/50">
                    <TableCell colSpan={COLUMNS} className="tw:py-1.5">
                      <button
                        type="button"
                        className="tw:text-xs tw:font-medium tw:underline-offset-2 tw:hover:underline"
                        onClick={() => onOpenGroup(group)}
                      >
                        {group.label}
                      </button>
                      <span className="tw:ml-2 tw:text-xs tw:text-muted-foreground tw:tabular-nums">
                        {group.tasks.length}
                      </span>
                    </TableCell>
                  </TableRow>
                  {group.tasks.map((task) => (
                    <TaskRow
                      key={`${group.key}-${task.action_id}`}
                      task={task}
                      onOpen={onOpenTask}
                    />
                  ))}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function TaskRow({
  task,
  onOpen,
}: {
  task: MyTask;
  onOpen: (task: MyTask) => void;
}) {
  const blockers = describeBlockers(task.blocked_by);
  const overdue = isOverdue(task);

  return (
    <TableRow
      role="button"
      tabIndex={0}
      aria-label={`Open ${task.name}`}
      className="tw:cursor-pointer"
      onClick={() => onOpen(task)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(task);
        }
      }}
    >
      <TableCell className="tw:align-top">
        <div className="tw:flex tw:flex-col tw:gap-0.5">
          <span className="tw:text-sm tw:leading-snug">{task.name}</span>
          {task.description ? (
            <span className="tw:line-clamp-1 tw:text-[11px] tw:text-muted-foreground">
              {task.description}
            </span>
          ) : null}
        </div>
      </TableCell>
      <TableCell className="tw:align-top">
        <Badge
          variant={PRIORITY_VARIANT[task.priority] ?? 'secondary'}
          className="tw:w-fit tw:text-[10px]"
        >
          {task.priority}
        </Badge>
      </TableCell>
      <TableCell className="tw:align-top">
        <div className="tw:flex tw:flex-col tw:items-start tw:gap-1">
          <span className="tw:text-xs">{task.status}</span>
          {blockers ? (
            <Badge
              variant="outline"
              className="tw:text-[10px]"
              title={blockers.title}
            >
              {blockers.label}
            </Badge>
          ) : null}
        </div>
      </TableCell>
      <TableCell className="tw:align-top tw:text-xs">
        {task.target_date ? (
          <span className={overdue ? 'tw:text-destructive' : undefined}>
            {formatDateTime(task.target_date)}
            {overdue ? ' (overdue)' : ''}
          </span>
        ) : (
          <span className="tw:text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="tw:align-top tw:text-xs tw:text-muted-foreground">
        {assignmentLabel(task)}
      </TableCell>
    </TableRow>
  );
}
